import type { Assessment } from "../types/assessment.js";
import type {
  PersonInvolvement,
  PersonInvolvementId,
} from "../types/involvement.js";
import { hasInvolvementEnded } from "./hasInvolvementEnded.js";

/** Who asked a guest to complete an assessment, and until when the guest has access. */
export type GuestAsk = {
  /** The guest's involvement, which ending their access ends. */
  involvementId: PersonInvolvementId;
  askerName: string;
  /** Null once the guest's access has ended, and where no end is set. */
  endsAt: Date | null;
};

/**
 * How long before its assessment a guest's involvement may have started and
 * still be the one asking for it. Asking starts both in one request, a moment
 * apart, so an involvement any older was started by a different act and did
 * not ask for this assessment.
 */
const askedWithinMilliseconds = 60_000;

/**
 * Who asked for an assessment a guest owns, read off the assessor
 * involvement its owner holds for no organisation that was started with the
 * assessment. Undefined for any other assessment, and while the involvements
 * are unread.
 */
export const guestAskOf = (
  assessment: Pick<Assessment, "ownerUserId" | "startedAt">,
  involvements:
    | readonly Pick<
        PersonInvolvement,
        | "id"
        | "kind"
        | "party"
        | "startedAt"
        | "startedByUserName"
        | "expiresAt"
        | "endedAt"
      >[]
    | undefined,
  now: Date = new Date(),
): GuestAsk | undefined => {
  const startedAt = assessment.startedAt.getTime();
  const asked = (involvements ?? [])
    .filter(
      (involvement) =>
        involvement.kind === "assessor" &&
        involvement.party.kind === "user" &&
        involvement.party.userId === assessment.ownerUserId &&
        involvement.party.onBehalfOfOrganisationId === null &&
        involvement.startedAt.getTime() <= startedAt &&
        startedAt - involvement.startedAt.getTime() <= askedWithinMilliseconds,
    )
    .toSorted((a, b) => b.startedAt.getTime() - a.startedAt.getTime())[0];

  return asked === undefined
    ? undefined
    : {
        involvementId: asked.id,
        askerName: asked.startedByUserName,
        endsAt: hasInvolvementEnded(asked, now) ? null : asked.expiresAt,
      };
};
